/**
 * Laatumatriisi — työvaiheet down the side, the five Congrid titles across the
 * top. A cell paints the state of every tarkastustehtävä it holds, so one look
 * tells which työvaiheet still have inspections open.
 *
 * Colour per item, first match wins:
 *   dark green  — Congrid's own inspection status is ACCEPTED
 *   light green — every topic on the task is COMPLETED
 *   grey        — no topic is COMPLETED, a task with no topics included
 *   yellow      — anything else, meaning partway through
 *
 * Grey is a lighter tint of the Ei aloitettu column on Tehtävähallinta.
 *
 * Liitedokumentit are document tasks, not Congrid inspections, so they carry no
 * inspection status and reach light green only through their subtasks.
 *
 * REJECTED, CANNOT_MEASURE and NOT_APPLICABLE count toward the total but never
 * toward done, so they hold a cell yellow. That is deliberately stricter than
 * the subtask-progress dataloader, which buckets them differently.
 *
 * A red dot marks myöhässä: a cell carries one when any inspection in it is
 * late, and each drill-down tile carries its own. A tile also names the
 * inspection's määräaika beside its topic count.
 */

import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { formatDateFi } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeftIcon } from "@/lib/icons";
import {
  useQualityMatrix,
  type QualityMatrixItem,
  type QualityMatrixRow,
} from "@/hooks/useQualityMatrix";

const MATRIX_TITLES = [
  "Mestan vastaanotto",
  "Malliasennustarkastus",
  "Osakohteen tarkastus",
  "Työvaiheenvastaanotto",
  "Liitedokumentit",
] as const;

const DASH = "–";

type Shade = "accepted" | "done" | "open" | "untouched";

const SHADE_BG: Record<Shade, string> = {
  accepted: "bg-[#4DBB63]",
  done: "bg-[#AEF359]",
  open: "bg-[#FFE14C]",
  untouched: "bg-slate-300",
};

function itemShade(item: QualityMatrixItem): Shade {
  if ((item.externalInspectionStatus ?? "").toUpperCase() === "ACCEPTED") {
    return "accepted";
  }
  if (item.totalTopics > 0 && item.doneTopics === item.totalTopics) {
    return "done";
  }
  if (item.doneTopics === 0) {
    return "untouched";
  }
  return "open";
}

/** Location hierarchy on a drill-down tile; the grandparent only for Tila and Alue. */
function locationLabel(item: QualityMatrixItem): string {
  if (!item.locationName) return DASH;
  return [item.grandparentLocationName, item.parentLocationName, item.locationName]
    .filter(Boolean)
    .join(" › ");
}

/**
 * Myöhässä, the same rule as the Tilannekatsaus deadline column: the määräaika
 * is strictly before today and the inspection has not landed. Accepted in
 * Congrid or every topic checked off both count as landed, so neither is ever
 * late. An item with no määräaika is never late, and time-of-day is ignored so
 * one due today is not yet late.
 */
function isItemLate(item: QualityMatrixItem): boolean {
  if (!item.dueDate) return false;
  const shade = itemShade(item);
  if (shade === "accepted" || shade === "done") return false;
  const due = new Date(item.dueDate).getTime();
  if (Number.isNaN(due)) return false;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return due < startOfToday.getTime();
}

/** The myöhässä marker, shared by the cells and the drill-down tiles. */
function LateDot({ className }: { className?: string }) {
  return (
    <span
      role="img"
      aria-label="Myöhässä"
      title="Myöhässä"
      className={cn(
        "h-1 w-1 shrink-0 rounded-full bg-red-500 ring-1 ring-red-500/25",
        className,
      )}
    />
  );
}

function sectionLabel(row: QualityMatrixRow): string {
  const code = row.code ?? "";
  const name = row.name ?? "";
  if (code && name) return `${code} - ${name}`;
  return code || name || DASH;
}

interface DrillDown {
  sectionLabel: string;
  title: string;
  items: QualityMatrixItem[];
}

/**
 * One rendered table row: either a päälittera group header or a työvaihe.
 * Headers hold no data — they only break the list into the same seven groups
 * the hanke summary card uses.
 */
type RenderEntry =
  | { kind: "header"; key: string; number: number; label: string }
  | { kind: "row"; key: string; row: QualityMatrixRow };

/** Insert a header ahead of each run of rows sharing a päälittera. */
function groupRows(rows: QualityMatrixRow[]): RenderEntry[] {
  const entries: RenderEntry[] = [];
  let current: number | null = null;
  let started = false;

  for (const row of rows) {
    if (!started || row.headsectionNumber !== current) {
      current = row.headsectionNumber;
      started = true;
      // A row whose code resolves to no päälittera gets no header rather than
      // an invented label; the backend sorts those last.
      if (row.headsectionNumber != null && row.headsectionLabel) {
        entries.push({
          kind: "header",
          key: `headsection-${row.headsectionNumber}`,
          number: row.headsectionNumber,
          label: row.headsectionLabel,
        });
      }
    }
    entries.push({ kind: "row", key: row.sectionId, row });
  }

  return entries;
}

interface MatrixRowProps {
  row: QualityMatrixRow;
  onCellClick: (
    row: QualityMatrixRow,
    title: string,
    items: QualityMatrixItem[],
  ) => void;
}

function MatrixRow({ row, onCellClick }: MatrixRowProps) {
  return (
    <tr className="h-6 border-t">
      <td className="sticky left-0 z-10 border-r bg-background px-3 py-0.5 text-xs font-[450]">
        {/* Fixed width + truncate, not wrapping: a long työvaihe name would
            otherwise make its row twice the height of the rest. Weight 450
            lands between normal and medium on a variable face and falls back to
            normal on static Aptos. */}
        <span className="block w-80 truncate" title={sectionLabel(row)}>
          {sectionLabel(row)}
        </span>
      </td>
      {MATRIX_TITLES.map((title) => {
        const cell = row.cells.find((c) => c.title === title);
        const items = cell?.items ?? [];
        if (items.length === 0) {
          return (
            <td key={title} className="border-r px-2 py-0.5 last:border-r-0" />
          );
        }
        const lateCount = items.filter(isItemLate).length;
        return (
          <td key={title} className="border-r px-2 py-0.5 last:border-r-0">
            <button
              type="button"
              onClick={() => onCellClick(row, title, items)}
              title={
                lateCount > 0
                  ? `${items.length} tarkastustehtävää, ${lateCount} myöhässä`
                  : `${items.length} tarkastustehtävää`
              }
              className="relative flex h-4 w-full overflow-hidden rounded-sm transition-opacity duration-150 hover:opacity-80 active:scale-[0.98]"
            >
              {items.map((item) => (
                <span
                  key={item.taskId}
                  className={cn("flex-1", SHADE_BG[itemShade(item)])}
                />
              ))}
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="rounded bg-white/80 px-1 text-[10px] font-medium leading-none text-slate-900">
                  {items.length}
                </span>
              </span>
              {/* Pinned right so it never fights the centred count. */}
              {lateCount > 0 && (
                <LateDot className="absolute right-1 top-1/2 -translate-y-1/2" />
              )}
            </button>
          </td>
        );
      })}
    </tr>
  );
}

interface QualityMatrixProps {
  projectId: string;
  onItemClick: (taskId: string) => void;
  registerRefetch: (refetch: (() => void) | null) => void;
}

export function QualityMatrix({
  projectId,
  onItemClick,
  registerRefetch,
}: QualityMatrixProps) {
  const { rows, loading, refetch } = useQualityMatrix(projectId);
  const [drill, setDrill] = useState<DrillDown | null>(null);

  useEffect(() => {
    registerRefetch(() => {
      void refetch();
    });
    return () => registerRefetch(null);
  }, [registerRefetch, refetch]);

  // Switching hanke must not leave the previous one's tiles on screen.
  useEffect(() => {
    setDrill(null);
  }, [projectId]);

  const hasAnyItem = useMemo(
    () => rows.some((row) => row.cells.some((cell) => cell.items.length > 0)),
    [rows],
  );

  const renderRows = useMemo(() => groupRows(rows), [rows]);

  const handleCellClick = (
    row: QualityMatrixRow,
    title: string,
    items: QualityMatrixItem[],
  ) => {
    if (items.length === 0) return;
    if (items.length === 1) {
      onItemClick(items[0].taskId);
      return;
    }
    setDrill({ sectionLabel: sectionLabel(row), title, items });
  };

  if (loading && rows.length === 0) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        Ei työvaiheita tällä hankkeella.
      </div>
    );
  }

  if (drill) {
    // gap-4 matches the mt-4 Tilannekatsaus puts between its header border and
    // this view, so the strip sits in equal air above and below.
    return (
      <div className="flex h-full flex-col gap-4">
        {/* One slim strip: the control and the breadcrumb share a 28px line, so
            nothing in it sets its own height. The breadcrumb truncates rather
            than pushing the control off the row. */}
        <div className="flex h-7 min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setDrill(null)}
            className="flex h-7 shrink-0 items-center gap-1 rounded-md border border-border/60 bg-muted/60 pl-1.5 pr-2.5 text-xs leading-none text-foreground transition-colors duration-150 hover:bg-muted active:scale-[0.97]"
          >
            <ChevronLeftIcon className="h-3.5 w-3.5" />
            Takaisin
          </button>
          <span className="min-w-0 truncate text-xs leading-none">
            <span className="font-medium text-foreground">
              {drill.sectionLabel}
            </span>
            <span className="px-1.5 text-muted-foreground/60">›</span>
            <span className="text-muted-foreground">{drill.title}</span>
          </span>
        </div>
        <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3 lg:grid-cols-4">
          {drill.items.map((item) => {
            const shade = itemShade(item);
            return (
              <button
                key={item.taskId}
                type="button"
                onClick={() => onItemClick(item.taskId)}
                className={cn(
                  "flex h-20 flex-col items-start justify-between rounded-md border p-3 text-left transition-opacity duration-150 hover:opacity-80 active:scale-[0.99]",
                  SHADE_BG[shade],
                )}
              >
                {/* Every text sits on the same translucent white plate the
                    cell counts use, so they stay readable on every shade. */}
                <span className="line-clamp-2 w-fit max-w-full rounded bg-white/80 px-1 text-xs font-medium text-slate-900">
                  {locationLabel(item)}
                </span>
                <span className="flex w-full min-w-0 items-center gap-1.5">
                  <span className="shrink-0 rounded bg-white/80 px-1 text-xs font-medium leading-tight text-slate-900">
                    {item.doneTopics}/{item.totalTopics}
                  </span>
                  {/* Määräaika. Tabular figures keep the dates aligned down a
                      column of tiles; myöhässä stays the dot's job, so a late
                      date is not recoloured on top of it. */}
                  <span className="min-w-0 truncate rounded bg-white/80 px-1 text-xs font-medium leading-tight tabular-nums text-slate-900">
                    {item.dueDate ? formatDateFi(item.dueDate) : DASH}
                  </span>
                  {isItemLate(item) && <LateDot />}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto rounded-md border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th
              className={cn(
                "sticky left-0 top-0 z-20 w-80 border-r bg-background px-3 py-1 text-left text-xs font-medium",
                "shadow-[inset_0_-1px_0_hsl(var(--border))]",
              )}
            >
              Työvaihe
            </th>
            {MATRIX_TITLES.map((title) => (
              <th
                key={title}
                className={cn(
                  "sticky top-0 z-10 min-w-32 border-r bg-background px-3 py-1 text-center text-xs font-medium last:border-r-0",
                  "shadow-[inset_0_-1px_0_hsl(var(--border))]",
                )}
              >
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {renderRows.map((entry) =>
            entry.kind === "header" ? (
              <tr key={entry.key} className="h-6 border-t bg-muted/60">
                <td
                  colSpan={1 + MATRIX_TITLES.length}
                  className="px-3 py-0.5 text-xs"
                >
                  {/* Pinned left so the group stays named while the columns
                      scroll sideways under it. */}
                  <span className="sticky left-3 inline-flex items-center gap-2 font-medium text-muted-foreground">
                    <span className="rounded bg-background px-1.5 font-mono">
                      {entry.number}
                    </span>
                    {entry.label}
                  </span>
                </td>
              </tr>
            ) : (
              <MatrixRow
                key={entry.key}
                row={entry.row}
                onCellClick={handleCellClick}
              />
            ),
          )}
        </tbody>
      </table>
      {!hasAnyItem && (
        <div className="py-8 text-center text-xs text-muted-foreground">
          Ei tarkastustehtäviä tällä hankkeella.
        </div>
      )}
    </div>
  );
}
